import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import type { Theme } from '@mui/material';
import { Box, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import type { MouseEvent } from 'react';
import type { SmartReportEntityExtended } from '@/admins/SmartReports/types';
import ErrorBoundary from '@/components/ErrorBoundary';
import { combineSx, sxVariants } from '@/utils/combineSx';
import { STATIC_END_PAGES } from '../constants';
import { SmartReportLayouts } from '../Layouts';
import type { SmartReportPageWithKey } from './EditSmartReportContext';
import { useEditSmartReport } from './EditSmartReportContext';

const styles = {
  wrapper: (theme: Theme) => ({
    position: 'relative',
    width: '100%',
    aspectRatio: '16/9',
    overflow: 'hidden',
    border: `${theme.palette.baseColors.black.c12} 0.125rem solid`,
    cursor: 'pointer',
    transition: 'border 0.32s var(--ease-enter)',
    backgroundColor: `${theme.palette.baseColors.white.c100}`,
    '&:hover': {
      borderColor: theme.palette.baseColors.infoBlue.c75,
      transition: 'border 0.32s var(--ease-exit)',
    },
  }),
  pageItem: {
    position: 'relative',
    width: '100%',
    scrollSnapAlign: 'start',
    '&:hover .deleteIcon': {
      opacity: 1,
      transition: 'opacity 0.2s var(--ease-enter)',
    },
  },
  pagePreview: {
    position: 'relative',
    '--factor': '8',
    transform: 'scale(calc(1 / var(--factor)))',
    transformOrigin: 'top left',
    width: 'calc(100% * var(--factor))',
    height: 'calc(100% * var(--factor))',
    pointerEvents: 'none',
  },
  deleteIcon: (theme: Theme) => ({
    position: 'absolute',
    top: '-0.5rem',
    right: '-0.5rem',
    backgroundColor: `${theme.palette.baseColors.white.c100}`,
    color: theme.palette.baseColors.errorRed.c100,
    border: `0.125rem solid ${theme.palette.baseColors.errorRed.c100}`,
    borderRadius: '50%',
    padding: '0.125rem',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s var(--ease-exit)',
  }),
  newPageButton: (theme: Theme) => ({
    width: '100%',
    aspectRatio: '16/9',
    border: `0.125rem dashed ${theme.palette.baseColors.infoBlue.c100}`,
    backgroundColor: `${theme.palette.baseColors.white.c100}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    transition: 'border-color 0.4s var(--ease-exit)',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `repeating-linear-gradient(
        135deg,
        ${theme.palette.baseColors.infoBlue.c100} 0,
        ${theme.palette.baseColors.infoBlue.c100} 0.0625rem,
        transparent 0.25rem,
        transparent 0.5rem
      )`,
      opacity: 0,
      transition: 'opacity 0.32s var(--ease-enter)',
    },
    '&:hover': {
      borderStyle: 'solid',
      '&::before': {
        opacity: 1,
      },
      '& .addIcon': {
        backgroundColor: theme.palette.baseColors.infoBlue.c100,
        boxShadow: `0 0 0 0.5rem ${theme.palette.baseColors.infoBlue.c100}40`,
        color: `${theme.palette.baseColors.white.c100}`,
      },
    },
  }),
  addIcon: (theme: Theme) => ({
    fontSize: '3rem',
    borderRadius: '50%',
    position: 'relative',
    zIndex: 1,
    color: theme.palette.baseColors.infoBlue.c100,
  }),
  pageItemNewButtonWrapper: {
    position: {
      xs: 'sticky',
      lg: 'relative',
    },
    right: 0,
    top: 0,
  },
  staticOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
    zIndex: 10,
  },
  pageNumber: (theme: Theme) => ({
    position: 'absolute',
    top: '0.5rem',
    left: '0.5rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.baseColors.black.c100,
    backgroundColor: theme.palette.baseColors.white.c100,
    border: `1px solid ${theme.palette.baseColors.black.c12}`,
    borderRadius: '0.25rem',
    padding: '0.125rem 0.5rem',
    zIndex: 2,
    boxShadow: `0 1px 0.125rem ${theme.palette.baseColors.black.c12}`,
    opacity: 'var(--pageNumberOpacity)',
    transition: 'opacity 0.2 var(--ease-move)',
  }),
};

const wrapperVariants = sxVariants({
  base: styles.wrapper,
  variants: {
    selected: {
      true: (theme: Theme) => ({
        border: `2px solid ${theme.palette.baseColors.infoBlue.c100}`,
      }),
      false: {},
    },
  },
});

type StaticPageItemProps = {
  PageComponent: React.ComponentType<{ pageNumber: number }>;
  pageNumber: number;
};

const StaticPageItem = ({ PageComponent, pageNumber }: StaticPageItemProps) => {
  return (
    <Box sx={styles.pageItem} data-id='static-page-item'>
      <Typography sx={styles.pageNumber}>{pageNumber}</Typography>
      <Tooltip title='Static mandatory page' placement='bottom'>
        <Box
          sx={combineSx(styles.wrapper, {
            overflow: 'hidden',
            position: 'relative',
            cursor: 'not-allowed',
          })}
          data-id='static-page-wrapper'
        >
          <Box sx={styles.pagePreview} data-id='static-page-preview'>
            <PageComponent pageNumber={pageNumber} />
            <Box sx={styles.staticOverlay} />
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

const NewPageItem = () => {
  const { addPage } = useEditSmartReport();

  return (
    <Box
      sx={combineSx(styles.pageItem, styles.pageItemNewButtonWrapper)}
      data-id='new-page-button-page-item'
    >
      <Box component='button' onClick={addPage} sx={styles.newPageButton} type='button'>
        <AddIcon className='addIcon' sx={styles.addIcon} />
      </Box>
    </Box>
  );
};

type PageItemProps = {
  page: SmartReportPageWithKey;
};

const PageItem = ({ page }: PageItemProps) => {
  const { selectedPage, setSelectedPage, removePage, entity } = useEditSmartReport();
  const layout =
    SmartReportLayouts.find(layout => layout.id === page?.layout) || SmartReportLayouts[0];
  const isSelected = page?.number === selectedPage?.number;

  const handlePageSelect = () => {
    setSelectedPage(page);
  };

  const handleRemovePage = (e: MouseEvent) => {
    e.stopPropagation();
    removePage(page);
  };

  const LayoutComponentView = layout?.ComponentView as React.ComponentType<{
    pageData: SmartReportPageWithKey;
    entity: SmartReportEntityExtended;
    mode?: string;
  }>;

  if (!LayoutComponentView || !entity) {
    return null;
  }

  return (
    <Box sx={styles.pageItem} data-id='preview-item'>
      <Typography sx={styles.pageNumber}>{page?.number}</Typography>
      <Box
        onClick={handlePageSelect}
        sx={wrapperVariants({ selected: isSelected })}
        data-id='preview-item-wrapper'
      >
        <Box sx={styles.pagePreview} data-id='preview-item-content'>
          <LayoutComponentView
            key={page?.number || 'default'}
            pageData={page}
            entity={entity}
            mode='preview'
          />
        </Box>
      </Box>
      <CloseIcon className='deleteIcon' sx={styles.deleteIcon} onClick={handleRemovePage} />
    </Box>
  );
};

export const Previews = () => {
  const { pages } = useEditSmartReport();

  return (
    <ErrorBoundary>
      {pages.map(page => (
        <PageItem key={`page-${page?.key}`} page={page} />
      ))}
      {STATIC_END_PAGES.map((PageComponent, index) => (
        <StaticPageItem
          PageComponent={PageComponent}
          pageNumber={(pages?.length || 0) + 1 + index}
          key={`static-page-${PageComponent.name}`}
        />
      ))}
      <NewPageItem />
    </ErrorBoundary>
  );
};
